import {KTSearchBox} from 'helpers/ui-components/KTSearchBox'
import {useListController} from 'hooks/useListController'
import {categoryList} from 'constants/common'
import React, {useState} from 'react'
import {useTranslation} from 'react-i18next'
import KTTable from 'helpers/ui-components/KTTable'
import {useNavigate} from 'react-router-dom'
import KTPagination from 'helpers/ui-components/KTPagination'
import {ColumnProps} from 'models/Common'
import Spinner from 'layout/common/Spinner'
import {
 fetchFestiveGreetings,
 updateFestiveApi,
} from 'utils/api/festiveGreetingService'
import {IFestiveGreeting} from 'models/festiveGreeting'

const FestiveGreetingListing = () => {
 const {t} = useTranslation()
 const navigate = useNavigate()
 const [selectedCategory, setSelectedCategory] =
  useState('')
 const [loader, setLoader] = useState(false)
 const {
  data: tableData,
  total: totalRecord,
  currentPage,
  itemsPerPage,
  setCurrentPage,
  setItemsPerPage,
  setSearchTerm,
  applyFilters,
  reload,
 } = useListController<IFestiveGreeting>({
  fetcher: fetchFestiveGreetings,
  initialPageSize: 10,
  setLoader,
 })

 const columns: Array<ColumnProps<IFestiveGreeting>> = [
  {
   key: 'id',
   title: t('festiveGreetings.greetingTable.srNo'),
   className: 'w-[70px] text-center',
   render: (_column, _record, index?: number) => {
    if (index === undefined) return '-'

    return (currentPage - 1) * itemsPerPage + index + 1
   },
  },
  {
   key: 'festival_name',
   title: (
    <div className='text-left'>
     {t('festiveGreetings.greetingTable.festivalName')}
    </div>
   ),
   render: (_, record) => (
    <>
     <p className='text-[#33393D]'>
      {record.festival_name}
     </p>
    </>
   ),
  },
  {
   key: 'is_active',
   title: t('festiveGreetings.greetingTable.isActive'),
   className: 'text-left',
   render: (_, record) => (
    <label className='inline-flex items-center cursor-pointer'>
     <input
      type='checkbox'
      checked={record.is_active}
      className='sr-only peer'
      onChange={async () => {
       const newStatus = !record.is_active
       const payload = {id: record.id, is_active: newStatus}
       const res = await updateFestiveApi(payload)

       if (res?.data) {
        void reload()
       }
      }}
     />
     <div className="w-10 h-5 bg-[#E6EBF0] border border-[#CED8E0] peer-focus:outline-none rounded-full relative transition-colors after:content-[''] after:absolute after:left-[2px] after:top-1/2 after:-translate-y-1/2 after:h-4 after:w-4 after:rounded-full after:bg-black after:transition-all peer-checked:bg-[#4E83E4] peer-checked:border-transparent peer-checked:after:bg-white peer-checked:after:translate-x-5" />
    </label>
   ),
  },
  {
   key: 'action',
   title: t('festiveGreetings.greetingTable.action'),
   className: 'text-center',
   render: (_, record) => {
    return (
     <div
      className='flex items-center justify-center w-full h-full'
      onClick={() =>
       navigate(`create-greeting/${record.id}`)
      }
     >
      <div className='!px-4 !py-1.5 text-[#4E83E4] border border-[#4E83E4] !rounded-lg cursor-pointer'>
       {t('festiveGreetings.greetingTable.viewDetails')}
      </div>
     </div>
    )
   },
  },
 ]

 const applyCategoryFilter = (type: string) => {
  setSelectedCategory(type)
  const filters: Record<string, boolean> = {}
  if (type === 'Inactive') {
   filters.is_active = false
  } else if (type === 'Active') {
   filters.is_active = true
  }
  applyFilters(filters)
 }
 return (
  <>
   {loader && (
    <div className='fixed inset-0 z-[9999] flex items-center justify-center bg-black/30'>
     <Spinner />
    </div>
   )}
   <>
    <div className='flex justify-between items-center'>
     <p style={{fontSize: 24, fontWeight: '700'}}>
      {t('festiveGreetings.title')}
     </p>
     <div
      onClick={() => {
       navigate('create-greeting')
      }}
      className='inline-flex items-center bg-[#5B7EE5]
               text-white font-semibold text-sm px-4 py-2 rounded-lg cursor-pointer'
     >
      <span className='text-lg leading-none mr-1'>+</span>
      {t('festiveGreetings.createFestiveGreeting')}
     </div>
    </div>
    <div
     className='mt-5 flex items-center gap-4'
     style={{
      padding: 8,
      borderRadius: 8,
      backgroundColor: '#F5F8FA',
     }}
    >
     <div
      className='flex-1'
      style={{backgroundColor: 'white'}}
     >
      <KTSearchBox
       label={t('festiveGreetings.greetingTable.search')}
       placeholder={t(
        'festiveGreetings.greetingTable.searchPlaceholder'
       )}
       setSearchTerm={setSearchTerm}
      />
     </div>

     <div className='flex items-center'>
      <div className={`text-[#33393D] text-sm mr-2`}>
       {t('common.quickFilters')}
      </div>
      {categoryList.map((val, key) => (
       <div
        key={key}
        onClick={() => applyCategoryFilter(val.key)}
        className={`${
         selectedCategory === val.key
          ? '!bg-[#4E83E4] text-white border-[#4E83E4]'
          : ''
        } text-center py-1.5 px-3 bg-white rounded-lg text-sm text-[#33393D] border border-[#CED8E0] hover:bg-[#4E83E4] hover:text-white cursor-pointer mr-2 w-[90px]`}
       >
        {val.value}
       </div>
      ))}
     </div>
    </div>
    <div className='mt-6'>
     <KTTable
      data={tableData}
      columns={columns}
      headerClass='bg-[#80868C]'
      rowClass='even:bg-[#F5F8FA]'
      colClass='!text-sm'
      showPagination={false}
     />
    </div>

    <div className='mt-4 flex flex-col'>
     <KTPagination
      currentPage={currentPage}
      itemsPerPage={itemsPerPage}
      totalRecord={totalRecord}
      onPageChange={setCurrentPage}
      setItemsPerPage={(size) => {
       setItemsPerPage(size)
       setCurrentPage(1)
      }}
     />
    </div>
   </>
  </>
 )
}

export default FestiveGreetingListing
